import type { DagDirection, DagSummaryGroup, DagViewMode } from "./viewProjections";
import {
  ALL_DAG_PATH_STATES,
  ALL_DAG_RECORD_TYPES,
  ALL_MODEL_CLASSIFICATIONS,
  DEFAULT_DAG_FILTERS,
  type DagFilterState,
  type DagTopologyFacet,
  type DagTimeWindowDays,
} from "./search";
import { parseTemporalCompareWindowInput, parseTemporalReplayInput } from "./temporalState";
import type { LineageDsmMatrixKind } from "../../api/lineageDsm";

export interface DagWorkspaceUrlState {
  mode: DagViewMode;
  direction: DagDirection;
  depth: number | null;
  compareA: string | null;
  compareB: string | null;
  timeCompareBeforeInput: string;
  timeCompareBefore: string | null;
  timeCompareAfterInput: string;
  timeCompareAfter: string | null;
  timeCompareError: string | null;
  summaryGroup: DagSummaryGroup;
  dsmMatrixKind: LineageDsmMatrixKind;
  dsmDrillToken: string | null;
  dsmCursor: string | null;
  dsmNavigationError: string | null;
  searchQuery: string;
  filters: DagFilterState;
  asOfInput: string;
  asOf: string | null;
  recordedAsOfInput: string | null;
  recordedAsOf: string | null;
  snapshotTokenInput: string | null;
  snapshotToken: string | null;
  temporalError: string | null;
}

export const DEFAULT_DAG_WORKSPACE_STATE: DagWorkspaceUrlState = {
  mode: "chronicle",
  direction: "both",
  depth: 2,
  compareA: null,
  compareB: null,
  timeCompareBeforeInput: "",
  timeCompareBefore: null,
  timeCompareAfterInput: "",
  timeCompareAfter: null,
  timeCompareError: null,
  summaryGroup: "category",
  dsmMatrixKind: "dependency",
  dsmDrillToken: null,
  dsmCursor: null,
  dsmNavigationError: null,
  searchQuery: "",
  filters: DEFAULT_DAG_FILTERS,
  asOfInput: "",
  asOf: null,
  recordedAsOfInput: null,
  recordedAsOf: null,
  snapshotTokenInput: null,
  snapshotToken: null,
  temporalError: null,
};

const VIEW_MODES = new Set<DagViewMode>(["chronicle", "flow", "focus", "impact", "compare", "time_compare", "summary", "dsm", "runs", "audit"]);
const DIRECTIONS = new Set<DagDirection>(["upstream", "both", "downstream"]);
const SUMMARY_GROUPS = new Set<DagSummaryGroup>(["category", "kind", "type", "state", "actor"]);
const RECORD_TYPES = new Set(ALL_DAG_RECORD_TYPES);
const PATH_STATES = new Set(ALL_DAG_PATH_STATES);
const MODEL_CLASSIFICATIONS = new Set(ALL_MODEL_CLASSIFICATIONS);
const TOPOLOGY = new Set<DagTopologyFacet>(["root", "leaf", "anchor"]);
const DSM_MATRIX_KINDS = new Set<LineageDsmMatrixKind>(["dependency", "model_context"]);

function dsmToken(value: string | null, field: string): { value: string | null; error: string | null } {
  if (value === null) return { value: null, error: null };
  const length = [...value].length;
  if (length < 20 || length > 2_048 || [...value].some((character) => character.charCodeAt(0) < 0x20 || character.charCodeAt(0) === 0x7f)) {
    return { value: null, error: `${field} is not a valid bounded DSM replay token.` };
  }
  return { value, error: null };
}

function listParam<T extends string>(params: URLSearchParams, name: string, allowed: ReadonlySet<T>, fallback: readonly T[]): T[] {
  if (!params.has(name)) return [...fallback];
  const raw = params.get(name) ?? "";
  if (!raw) return [];
  const values = raw.split(",").filter((value): value is T => allowed.has(value as T));
  return [...new Set(values)];
}

function timeWindow(value: string | null): DagTimeWindowDays {
  const parsed = Number(value);
  return parsed === 7 || parsed === 30 || parsed === 90 ? parsed : null;
}

export function parseDagWorkspaceState(search: string): DagWorkspaceUrlState {
  const params = new URLSearchParams(search);
  const requestedMode = params.get("dagView") as DagViewMode | null;
  const requestedDirection = params.get("dagDir") as DagDirection | null;
  const requestedGroup = params.get("dagGroup") as DagSummaryGroup | null;
  const requestedDsmKind = params.get("dagDsmKind") as LineageDsmMatrixKind | null;
  const rawDepth = params.get("dagDepth");
  const parsedDepth = rawDepth === "all" ? null : Number(rawDepth);
  const asOfInput = params.get("dagAsOf") ?? "";
  const recordedAsOfInput = params.get("dagRecordedAsOf");
  const snapshotTokenInput = params.get("dagSnapshotToken");
  const temporal = parseTemporalReplayInput(asOfInput, recordedAsOfInput, snapshotTokenInput);
  const timeCompareBeforeInput = params.get("dagBeforeAsOf") ?? "";
  const timeCompareAfterInput = params.get("dagAfterAsOf") ?? "";
  const timeCompare = parseTemporalCompareWindowInput(
    timeCompareBeforeInput,
    timeCompareAfterInput,
    temporal.recordedAsOf,
  );
  const drill = dsmToken(params.get("dagDsmDrill"), "DSM drill path");
  const cursor = dsmToken(params.get("dagDsmCursor"), "DSM leaf cursor");
  const dsmNavigationError = drill.error
    ?? cursor.error
    ?? (cursor.value !== null && drill.value === null ? "A DSM leaf cursor requires its signed drill path." : null)
    ?? (drill.value !== null && (temporal.normalized === null || temporal.recordedAsOf === null || temporal.snapshotToken === null)
      ? "A DSM drill path requires the exact effective, recorded, and snapshot replay trio."
      : null);
  return {
    mode: requestedMode && VIEW_MODES.has(requestedMode) ? requestedMode : DEFAULT_DAG_WORKSPACE_STATE.mode,
    direction: requestedDirection && DIRECTIONS.has(requestedDirection) ? requestedDirection : DEFAULT_DAG_WORKSPACE_STATE.direction,
    depth: rawDepth === "all"
      ? null
      : parsedDepth != null && [1, 2, 3].includes(parsedDepth)
        ? parsedDepth
        : DEFAULT_DAG_WORKSPACE_STATE.depth,
    compareA: params.get("dagA") || null,
    compareB: params.get("dagB") || null,
    timeCompareBeforeInput,
    timeCompareBefore: timeCompare.before,
    timeCompareAfterInput,
    timeCompareAfter: timeCompare.after,
    timeCompareError: timeCompare.error,
    summaryGroup: requestedGroup && SUMMARY_GROUPS.has(requestedGroup) ? requestedGroup : DEFAULT_DAG_WORKSPACE_STATE.summaryGroup,
    dsmMatrixKind: requestedDsmKind && DSM_MATRIX_KINDS.has(requestedDsmKind) ? requestedDsmKind : DEFAULT_DAG_WORKSPACE_STATE.dsmMatrixKind,
    dsmDrillToken: drill.value,
    dsmCursor: cursor.value,
    dsmNavigationError,
    searchQuery: params.get("dagQuery") ?? "",
    filters: {
      recordTypes: listParam(params, "dagTypes", RECORD_TYPES, ALL_DAG_RECORD_TYPES),
      pathStates: listParam(params, "dagStates", PATH_STATES, ALL_DAG_PATH_STATES),
      modelClassifications: (() => {
        const values = listParam(params, "dagModels", MODEL_CLASSIFICATIONS, ALL_MODEL_CLASSIFICATIONS);
        return values.length ? ALL_MODEL_CLASSIFICATIONS.filter((value) => values.includes(value)) : [...ALL_MODEL_CLASSIFICATIONS];
      })(),
      topology: listParam(params, "dagTopology", TOPOLOGY, []),
      timeWindowDays: timeWindow(params.get("dagWindow")),
    },
    asOfInput,
    asOf: temporal.normalized,
    recordedAsOfInput,
    recordedAsOf: temporal.recordedAsOf,
    snapshotTokenInput,
    snapshotToken: temporal.snapshotToken,
    temporalError: temporal.error,
  };
}

function sameSet<T extends string>(left: readonly T[], right: readonly T[]): boolean {
  return left.length === right.length && left.every((value) => right.includes(value));
}

function writeDagWorkspaceUrl(state: DagWorkspaceUrlState, method: "replace" | "push"): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (state.mode === DEFAULT_DAG_WORKSPACE_STATE.mode) url.searchParams.delete("dagView");
  else url.searchParams.set("dagView", state.mode);
  if (state.direction === DEFAULT_DAG_WORKSPACE_STATE.direction) url.searchParams.delete("dagDir");
  else url.searchParams.set("dagDir", state.direction);
  if (state.depth === DEFAULT_DAG_WORKSPACE_STATE.depth) url.searchParams.delete("dagDepth");
  else url.searchParams.set("dagDepth", state.depth == null ? "all" : String(state.depth));
  if (state.compareA) url.searchParams.set("dagA", state.compareA); else url.searchParams.delete("dagA");
  if (state.compareB) url.searchParams.set("dagB", state.compareB); else url.searchParams.delete("dagB");
  if (state.timeCompareBeforeInput.trim()) {
    url.searchParams.set(
      "dagBeforeAsOf",
      state.timeCompareError === null
        ? state.timeCompareBefore ?? state.timeCompareBeforeInput
        : state.timeCompareBeforeInput,
    );
  } else url.searchParams.delete("dagBeforeAsOf");
  if (state.timeCompareAfterInput.trim()) {
    url.searchParams.set(
      "dagAfterAsOf",
      state.timeCompareError === null
        ? state.timeCompareAfter ?? state.timeCompareAfterInput
        : state.timeCompareAfterInput,
    );
  } else url.searchParams.delete("dagAfterAsOf");
  if (state.summaryGroup === DEFAULT_DAG_WORKSPACE_STATE.summaryGroup) url.searchParams.delete("dagGroup");
  else url.searchParams.set("dagGroup", state.summaryGroup);
  if (state.dsmMatrixKind === DEFAULT_DAG_WORKSPACE_STATE.dsmMatrixKind) url.searchParams.delete("dagDsmKind");
  else url.searchParams.set("dagDsmKind", state.dsmMatrixKind);
  if (state.dsmDrillToken) url.searchParams.set("dagDsmDrill", state.dsmDrillToken); else url.searchParams.delete("dagDsmDrill");
  if (state.dsmCursor) url.searchParams.set("dagDsmCursor", state.dsmCursor); else url.searchParams.delete("dagDsmCursor");
  if (state.searchQuery) url.searchParams.set("dagQuery", state.searchQuery); else url.searchParams.delete("dagQuery");
  if (sameSet(state.filters.recordTypes, DEFAULT_DAG_FILTERS.recordTypes)) url.searchParams.delete("dagTypes");
  else url.searchParams.set("dagTypes", state.filters.recordTypes.join(","));
  if (sameSet(state.filters.pathStates, DEFAULT_DAG_FILTERS.pathStates)) url.searchParams.delete("dagStates");
  else url.searchParams.set("dagStates", state.filters.pathStates.join(","));
  if (sameSet(state.filters.modelClassifications, DEFAULT_DAG_FILTERS.modelClassifications)) url.searchParams.delete("dagModels");
  else url.searchParams.set(
    "dagModels",
    ALL_MODEL_CLASSIFICATIONS.filter((value) => state.filters.modelClassifications.includes(value)).join(","),
  );
  if (!state.filters.topology.length) url.searchParams.delete("dagTopology");
  else url.searchParams.set("dagTopology", state.filters.topology.join(","));
  if (state.filters.timeWindowDays == null) url.searchParams.delete("dagWindow");
  else url.searchParams.set("dagWindow", String(state.filters.timeWindowDays));
  if (state.asOfInput.trim()) url.searchParams.set("dagAsOf", state.asOf ?? state.asOfInput);
  else url.searchParams.delete("dagAsOf");
  if (state.recordedAsOfInput) url.searchParams.set("dagRecordedAsOf", state.recordedAsOfInput);
  else url.searchParams.delete("dagRecordedAsOf");
  if (state.snapshotTokenInput) url.searchParams.set("dagSnapshotToken", state.snapshotTokenInput);
  else url.searchParams.delete("dagSnapshotToken");
  const nextLocation = `${url.pathname}${url.search}${url.hash}`;
  const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (nextLocation === currentLocation) return;
  if (method === "push") window.history.pushState(window.history.state, "", nextLocation);
  else window.history.replaceState(window.history.state, "", nextLocation);
  // Keep router-owned search-param snapshots in sync with the viewer's
  // router-neutral URL codec so a later node selection cannot drop dag state.
  window.dispatchEvent(new PopStateEvent("popstate", { state: window.history.state }));
}

/** Replace transient workspace state without creating noisy history entries. */
export function updateDagWorkspaceUrl(state: DagWorkspaceUrlState): void {
  writeDagWorkspaceUrl(state, "replace");
}

/** Commit one user-selected, exact temporal cut as a browser-history boundary. */
export function pushDagWorkspaceUrl(state: DagWorkspaceUrlState): void {
  writeDagWorkspaceUrl(state, "push");
}
